import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { analytics } from 'analytics';
import { toaster } from 'ui/Toaster';
import { formToSankey, sankeyToForm } from 'utils/form';

import { useApiContext } from '../../contexts/apiContext';
import { useSankeyExcelBindingContext } from '../../contexts/sankeyExcelBindingContext';
import { useSankeyFormContext } from '../../contexts/sankeyFormContext';
import { selectionToFlows } from '../../utils/addin/sankey';
import { pathToSankey } from '../../utils/routes';
import {
  buildExcelSankey,
  sankeyFormFlowsArrayToMap,
} from '../../utils/sankey';
import { useTeamContext } from '../../utils/team';
import { useCurrentUser } from '../../utils/user';
import { updateNodesFromFlows } from '../SankeyChart/utils';
import { useNewSankeyForm } from '../SankeyFormProvider';

export const useDraftFormBasedOnCellsSelection = (
  isCreatingNewSankey: boolean
) => {
  const { teamSettings } = useTeamContext();

  const draftForm = useNewSankeyForm();
  const { form: currentForm } = useSankeyFormContext();
  const { selectedValues, isRangeSelectionValid } =
    useSankeyExcelBindingContext();

  useEffect(() => {
    // If creating a new Sankey, reset the form to the initial Excel diagram,
    // otherwise keep the current form values as the base.
    if (isCreatingNewSankey) {
      draftForm.reset(sankeyToForm(buildExcelSankey()));
    } else {
      draftForm.reset(currentForm.getValues());
    }
    // If the selection is valid, apply flows data on top of the inital form.
    if (isRangeSelectionValid && selectedValues) {
      draftForm.setValue(
        'flows',
        sankeyFormFlowsArrayToMap(selectionToFlows(selectedValues)),
        { shouldDirty: true }
      );
      updateNodesFromFlows(draftForm, teamSettings);
    }
  }, [isCreatingNewSankey, selectedValues, teamSettings]);

  return draftForm;
};

export const useCreateFromExcelMutation = (form: SankeyForm) => {
  const navigate = useNavigate();
  const { api } = useApiContext();
  const { refetchUser } = useCurrentUser();
  const { bindDiagramToCurrentExcelSelection } = useSankeyExcelBindingContext();

  return useMutation({
    mutationFn: async () => {
      try {
        const { data: sankey } = await api().sankeys.create({
          sankeyRequest: formToSankey(form),
        });
        await refetchUser();
        await bindDiagramToCurrentExcelSelection(sankey.id);
        toaster.success({ title: 'New diagram created successfully' });
        navigate(pathToSankey(sankey.id));
      } catch (error) {
        analytics.reportError('Error creating diagram from Excel data', {
          error,
        });
      }
    },
  });
};
